import { clearAuthSession, getAuthHeaders, rememberAuthReturnPath } from './auth';

export const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/$/, '');

let activeOrganizationId = null;

export function setActiveOrganizationId(organizationId) {
  activeOrganizationId = organizationId ? String(organizationId) : null;
}

export function getActiveOrganizationId() {
  return activeOrganizationId;
}

export function apiUrl(path = '') {
  const rawPath = path || '';
  const normalizedPath = API_BASE.endsWith('/api')
    ? rawPath === '/api'
      ? ''
      : rawPath.startsWith('/api/')
        ? rawPath.slice(4)
        : rawPath
    : rawPath;

  if (!API_BASE) return normalizedPath || '/';
  if (!normalizedPath) return API_BASE;
  return `${API_BASE}${normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`}`;
}

export class ApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

export class UnauthorizedError extends ApiError {
  constructor(message = 'Session expired. Please sign in again.', data = null) {
    super(message, 401, data);
    this.name = 'UnauthorizedError';
  }
}

async function readResponseBody(res) {
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return res.json().catch(() => null);
  }
  return res.text().catch(() => null);
}

// A server error page (HTML) or a long text body is never a readable message;
// surface a short status line instead of dumping markup into the UI.
function readableTextError(data, status) {
  if (typeof data !== 'string' || !data.trim()) return `Request failed (HTTP ${status}).`;
  const text = data.trim();
  if (/^<!doctype|<html[\s>]/i.test(text) || text.length > 300) {
    return `The server could not complete this request (HTTP ${status}). Please try again.`;
  }
  return text;
}

export async function parseApiResponse(res) {
  const data = await readResponseBody(res);
  if (res.status === 401) {
    clearAuthSession();
    throw new UnauthorizedError((data && data.error) || 'Session expired. Please sign in again.', data);
  }
  if (!res.ok) {
    throw new ApiError((data && data.error) || readableTextError(data, res.status), res.status, data);
  }
  return data;
}

export async function apiFetch(path, options = {}) {
  const organizationHeaders = activeOrganizationId
    ? { 'X-Organization-ID': activeOrganizationId }
    : {};
  const headers = getAuthHeaders({ ...organizationHeaders, ...(options.headers || {}) });
  return fetch(apiUrl(path), { ...options, headers });
}

const RETRYABLE_READ_STATUSES = new Set([500, 502, 503, 504]);
export const READ_RETRY_DELAY_MS = 400;

// Reads are safe to repeat, so one retry absorbs a momentary server or
// database-connection drop. Writes and paid runs are never retried here.
async function fetchWithReadRetry(path, options) {
  const method = String(options.method || 'GET').toUpperCase();
  const retryable = method === 'GET' || method === 'HEAD';
  try {
    const res = await apiFetch(path, options);
    if (!retryable || !RETRYABLE_READ_STATUSES.has(res.status)) return res;
  } catch (networkError) {
    if (!retryable) throw networkError;
  }
  await new Promise((resolve) => setTimeout(resolve, READ_RETRY_DELAY_MS));
  return apiFetch(path, options);
}

export async function apiJson(path, options = {}) {
  const res = await fetchWithReadRetry(path, options);
  try {
    return await parseApiResponse(res);
  } catch (error) {
    if (isUnauthorizedError(error) && typeof window !== 'undefined' && window.location.pathname !== '/login') {
      rememberAuthReturnPath();
      window.location.assign('/login');
    }
    throw error;
  }
}

export function isUnauthorizedError(error) {
  return error instanceof UnauthorizedError || error?.status === 401 || error?.name === 'UnauthorizedError';
}
